import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import useSweepNavigate from './useSweepNavigate';
import { useSelector } from 'react-redux';
import { selectDefaultCreationCrmOrgId } from '../reducers/userInfoReducer';
import { DeepLinkSearchParams } from '@server/deep-link-search-params';
import usePrevious from '../hooks/usePrevious';
import { useSweepParams } from './useSweepParams';

type QueryParams = {
  [key: string]: string | undefined;
};

const useQueryParamsManager = (queryParams: QueryParams) => {
  const { crmOrgId: paramsCrmOrgId } = useSweepParams<{ crmOrgId: string }>();
  const includeCrmOrgId = !Boolean(paramsCrmOrgId);

  const defaultCrmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const _params = useMemo(() => {
    if (includeCrmOrgId) {
      return {
        ...queryParams,
        [DeepLinkSearchParams.crmOrgId]: defaultCrmOrgId ?? undefined,
      };
    }
    return queryParams;
  }, [queryParams, defaultCrmOrgId, includeCrmOrgId]);

  const { pathname } = useLocation();

  const { removeQueryParams, navigate } = useSweepNavigate();

  const set = useCallback(() => {
    const filteredSearchParams = Object.fromEntries(
      Object.entries(_params).filter(([, value]) => value !== undefined),
    ) as Record<string, string>;

    const searchParams = new URLSearchParams(window.location.search);
    Object.entries(filteredSearchParams).forEach(([key, value]) => {
      searchParams.set(key, value);
    });
    navigate(`${pathname}?${searchParams.toString()}`);
  }, [_params, navigate, pathname]);

  const remove = useCallback(() => {
    removeQueryParams(Object.keys(_params));
  }, [_params, removeQueryParams]);

  return { set, remove };
};

export const useSetQueryParamsOnMount = (queryParams: QueryParams, bypassEntirely?: boolean) => {
  const { set, remove } = useQueryParamsManager(queryParams);
  const previousQueryParams = usePrevious(JSON.stringify(queryParams));
  const didQueryParamsChange = useMemo(() => {
    return previousQueryParams !== JSON.stringify(queryParams);
  }, [previousQueryParams, queryParams]);

  useEffect(() => {
    if (bypassEntirely) {
      return;
    }

    set();

    return remove;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [didQueryParamsChange]);
};
